// 全局反馈抽屉组件的类型定义

// 反馈来源类型枚举
export enum SourceType {
  Feedback = 1, // 个人中心
  Search = 2, // 搜索结果页
  Recommend = 3, // 推荐结果页
  Product = 4, // 商详
  Recommend_Feedback = 5, // 推荐反馈
  Search_Feedback = 6, // 搜索反馈
  Category_Feedback = 7, // 类目搜索反馈
  Login_Feedback = 8, // 注册登录反馈
  ProductGeneral = 9, // 商详-一般问题
  ProductTranslate = 10, // 商详-翻译问题
  ProductReviewGeneral = 11, // 商详评论-一般问题
  ProductReviewTranslate = 12, // 商详评论-翻译问题
  Marketing_Feedback = 13, // 营销反馈
  ScreenShot = 14, // 截图
}

// 反馈来源类型描述映射
export const SOURCE_TYPE_DESCRIPTIONS = {
  [SourceType.Feedback]: { code: 'ME', type: '个人中心' },
  [SourceType.Search]: { code: 'Search', type: '搜索结果页' },
  [SourceType.Recommend]: { code: 'Recommend', type: '推荐结果页' },
  [SourceType.Product]: { code: 'Products', type: '商详' },
  [SourceType.Recommend_Feedback]: { code: 'Recommend_Feedback', type: '推荐反馈' },
  [SourceType.Search_Feedback]: { code: 'Search_Feedback', type: '搜索反馈' },
  [SourceType.Category_Feedback]: { code: 'Category_Feedback', type: '类目搜索反馈' },
  [SourceType.Login_Feedback]: {
    code: 'Login&Registration_Feedback',
    type: '注册登录反馈',
  },
  [SourceType.ProductGeneral]: {
    code: 'Product_General_Issue',
    type: '商详-一般问题',
  },
  [SourceType.ProductTranslate]: {
    code: 'Product_Translate_Issue',
    type: '商详-翻译问题',
  },
  [SourceType.ProductReviewGeneral]: {
    code: 'Comment_General_Issue',
    type: '商详评论-一般问题',
  },
  [SourceType.ProductReviewTranslate]: {
    code: 'Commnet_Translate_Issue',
    type: '商详评论-翻译问题',
  },
  [SourceType.Marketing_Feedback]: { code: 'Marketing_Feedback', type: '营销反馈' },
  [SourceType.ScreenShot]: { code: 'ScreenShot', type: '截图' },
};

// 反馈原因接口
export interface FeedbackReason {
  id: string;
  title: string;
  description?: string;
}

// 反馈表单数据接口
export interface FeedbackFormData {
  reasonId: string;
  content: string;
  additionalData?: Record<string, unknown>; // 额外数据，如被举报的内容、商品信息等
  sourceType?: SourceType; // 反馈来源类型
  starScore?: number; // 星级评分（1-5）
}

// 提交结果接口
export interface FeedbackSubmitResult {
  success: boolean;
  message?: string;
  data?: Record<string, unknown>;
}

// 目标数据接口（被反馈的对象信息）
export interface FeedbackTargetData {
  // 通用字段
  id?: string | number;
  type?: string;

  // 评论相关
  commentId?: string | number;
  userId?: string;
  userPin?: string;
  content?: string;
  imgList?: string[];
  videoList?: string[];

  // 商品相关
  skuId?: string | number;
  productName?: string;

  // 订单相关
  orderId?: string | number;

  // 页面相关
  pageType?: string;
  pageUrl?: string;

  // 其他自定义数据
  [key: string]: unknown;
}

// 用户信息接口
export interface UserInfo {
  userPin?: string; // 用户pin
  email?: string; // 用户邮箱
  phone?: string; // 用户电话
}

// 全局反馈抽屉组件Props接口
export interface GlobalFeedbackDrawerProps {
  // 基础控制属性
  openDrawer: boolean;
  setOpenDrawer: (open: boolean) => void;

  // 标题和类型
  title?: string;
  sourceType: SourceType; // 反馈来源类型（必填）

  // 原因列表
  reasonList?: FeedbackReason[];
  customReasons?: FeedbackReason[]; // 自定义原因列表，优先级高于reasonList

  // 反馈上下文数据
  contextData?: FeedbackTargetData;

  // 用户信息（用于提交API）
  userInfo?: UserInfo;

  // 回调函数
  afterSubmitCb?: (formData: FeedbackFormData) => void; // 提交成功后的自定义回调

  // 文本配置
  submitButtonText?: string;

  // 弹框配置（移动端用抽屉配置）
  dialogMaxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  drawerHeight?: number; // 移动端特有
  bottomBarHeight?: number; // 移动端特有

  // 评分配置
  showScore?: boolean; // 是否展示评分功能（默认false）

  // 埋点配置
  trackingData?: {
    exptag?: string;
    config?: string;
    param?: string;
  };
}

// ========== API 接口类型定义 ==========

// 反馈主题接口（与现有API保持一致）
export interface SubjectInfoDTO {
  subject: string; // 反馈主题名称
  subjectType: number; // 反馈主题类型
  showInputBox: boolean; // 是否展示输入框，Other 类型需要
}

// 反馈来源信息接口（与现有API保持一致）
export interface SourceInfoDTO {
  sourceType: number; // 反馈来源类型
  subjectInfoDTOList: SubjectInfoDTO[]; // 反馈主题列表
  showScore?: boolean; // 是否展示评分，默认为true（可选字段，兼容现有API）
}

// API 通用返回结果（与现有API保持一致）
export interface ResultModel<T = Record<string, unknown>> {
  success: boolean; // 查询是否成功
  code: string; // 状态码，用于指示查询结果的状态，200-成功
  message: string; // 状态消息，用于描述查询结果的状态
  data: T; // 包含查询结果的数据
}

// 现有API的响应接口（与 ResultModel<SourceInfoDTO> 兼容）
export type FeedbackGetListResponse = ResultModel<SourceInfoDTO>;

// 查询反馈来源和主题的API接口类型
export type GetFeedbackSourceAndSubjectsAPI = (
  sourceType: SourceType,
) => Promise<FeedbackGetListResponse>;

// ========== 提交API类型定义 ==========

// 导入API接口类型
export type {
  FeedbackSubmitVO,
  FeedbackSubmitResponse,
  FeedbackGetListRequest,
} from './apis/feedback.interface';

// 扩展的反馈原因接口，兼容API返回的主题数据
export interface ExtendedFeedbackReason extends FeedbackReason {
  subjectType?: number; // 反馈主题类型
  showInputBox?: boolean; // 是否展示输入框
}

// ========== 工具函数 ==========

/**
 * 将API返回的SubjectInfoDTO转换为FeedbackReason
 * @param subjects API返回的主题列表
 * @returns 转换后的反馈原因列表
 */
export function convertSubjectsToReasons(subjects: SubjectInfoDTO[]): ExtendedFeedbackReason[] {
  return subjects.map((subject) => ({
    id: subject.subjectType.toString(), // 使用subjectType作为id
    title: subject.subject,
    subjectType: subject.subjectType,
    showInputBox: subject.showInputBox,
  }));
}

/**
 * 获取来源类型的描述
 * @param sourceType 来源类型
 * @returns 来源类型描述
 */
export function getSourceTypeDescription(sourceType: SourceType): { code: string; type: string } {
  return SOURCE_TYPE_DESCRIPTIONS[sourceType] || { code: 'Unknown', type: '未知类型' };
}

/**
 * 判断反馈类型是否需要登录
 * @param sourceType 来源类型
 * @returns 是否需要登录
 */
export function isLoginRequired(sourceType: SourceType): boolean {
  // 只有 Login_Feedback 类型不需要登录，其他所有类型都需要登录
  return sourceType !== SourceType.Login_Feedback;
}
